import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
const _excluded = ["readOnly", "required", "extraOptions", "defaultOptions", "onChange"];
import { Component, useState } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { debounce, isEqual } from 'underscore';
import { TruncateString } from 'foundations-components/transitional/components/TruncateString';
import { Small } from 'foundations-components/transitional/components/Small';
import { Tooltip } from 'foundations-components/transitional/components/Tooltip';
import { Link } from 'foundations-components/transitional/components/Link';
import AsyncSelector from 'ContentComponents/components/cmv2/components/AsyncSelector';
// @ts-expect-error Not migrated yet
import PreviewFullscreenModal from 'ContentComponents/components/preview/PreviewFullscreenModal';
import ContentRoutes from 'ContentUtils/Routes';
import * as WebInteractivesActions from 'ContentData/actions/WebInteractives';
import * as webInteractivesSelectors from 'ContentData/selectors/webInteractives';
import { DEFAULT_PAGE_SIZE, ASYNC_SELECTOR_DEBOUNCE } from 'ContentComponents/components/cmv2/constants';
import { View } from 'foundations-components/components/View';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
function mapStateToProps(state) {
  return {
    options: webInteractivesSelectors.getWebInteractiveOptions(state),
    webInteractives: webInteractivesSelectors.getWebInteractives(state),
    defaultOptions: webInteractivesSelectors.getDefaultWebInteractiveOptions(state)
  };
}
const mapDispatchToProps = {
  fetchWebInteractive: WebInteractivesActions.fetchWebInteractive,
  fetchWebInteractives: WebInteractivesActions.fetchWebInteractives,
  saveCache: WebInteractivesActions.saveCache,
  clearCache: WebInteractivesActions.clearCache
};
const PreviewButton = ({
  pageContext = {},
  contentId
}) => {
  const [isOpen, setIsOpen] = useState(false);
  return /*#__PURE__*/_jsxs("div", {
    children: [/*#__PURE__*/_jsx(Link, {
      onClick: () => setIsOpen(true),
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "ContentComponents.cmv2.fields.call_to_action.preview"
      })
    }), isOpen && /*#__PURE__*/_jsx(PreviewFullscreenModal, {
      contentType: "web-interactives",
      handleClose: () => setIsOpen(false),
      previewUrl: ContentRoutes.getWebInteractivePreviewUrl(contentId, pageContext)
    })]
  });
};
class WebInteractiveSelect extends Component {
  constructor(props) {
    super(props);
    this.loadOptions = this.loadOptions.bind(this);
    this.debouncedLoadOptions = debounce(this.loadOptions, ASYNC_SELECTOR_DEBOUNCE);
    this.renderOption = this.renderOption.bind(this);
    this.state = {
      input: ''
    };
  }
  componentDidMount() {
    if (this.props.value) {
      this.props.fetchWebInteractive(this.props.value);
    }
  }
  componentDidUpdate(prevProps) {
    const {
      fetchWebInteractive,
      webInteractives,
      value
    } = this.props;
    const {
      value: prevValue
    } = prevProps;
    if (value && !isEqual(prevValue, value) && !webInteractives[value]) fetchWebInteractive(value);
  }
  loadOptions(input, callback, pagination = {
    offset: 0
  }) {
    let offset = pagination.offset;
    if (input !== this.state.input) {
      this.setState({
        input
      });
      this.props.clearCache();
      offset = 0;
    }
    const query = {
      name: input,
      containerTypes: ['BANNER', 'MODAL', 'SLIDE_IN'],
      count: DEFAULT_PAGE_SIZE,
      offset
    };
    this.props.fetchWebInteractives(query).then(response => {
      const {
        total,
        hasMore
      } = response;
      callback(null, {
        options: this.props.options,
        pagination: {
          hasMore,
          offset: offset + DEFAULT_PAGE_SIZE,
          length: total
        }
      });
    }).catch(err => {
      setTimeout(() => {
        throw err;
      });
    });
  }
  renderOption(option) {
    const {
      disabled,
      dropdownText,
      text,
      help
    } = option;
    const renderedOption = /*#__PURE__*/_jsxs(View, {
      "data-title": text,
      marginInline: "400",
      paddingBlock: "200",
      as: "div",
      children: [/*#__PURE__*/_jsx("div", {
        children: /*#__PURE__*/_jsx(TruncateString, {
          tooltip: !disabled,
          children: text || dropdownText
        })
      }), /*#__PURE__*/_jsx(TruncateString, {
        tooltip: !disabled,
        children: /*#__PURE__*/_jsx(Small, {
          children: help
        })
      })]
    });
    if (option.disabled) {
      return /*#__PURE__*/_jsx(Tooltip, {
        title: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "ContentComponents.cmv2.fields.callToAction.disabledTooltip"
        }),
        children: renderedOption
      });
    }
    return renderedOption;
  }
  render() {
    const _this$props = this.props,
      {
        readOnly,
        required,
        extraOptions,
        defaultOptions,
        onChange
      } = _this$props,
      rest = _objectWithoutPropertiesLoose(_this$props, _excluded);
    const newDefaultOptions = defaultOptions.concat(extraOptions || []);
    return /*#__PURE__*/_jsxs("div", {
      children: [/*#__PURE__*/_jsx(AsyncSelector, Object.assign({
        "data-selenium": "callToAction-select",
        clearable: !required,
        onChange: onChange,
        loadOptions: this.debouncedLoadOptions,
        defaultOptions: newDefaultOptions,
        optionRenderer: this.renderOption,
        optionRendererIgnoresSpecialOptions: true,
        readOnly: readOnly,
        type: "call_to_action"
      }, rest)), this.props.value && /*#__PURE__*/_jsx(PreviewButton, {
        contentId: this.props.value,
        pageContext: this.props.pageContext
      })]
    });
  }
}
WebInteractiveSelect.propTypes = {
  readOnly: PropTypes.bool,
  required: PropTypes.bool.isRequired,
  useOnDark: PropTypes.bool,
  /* Optional url from forms */
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  extraOptions: PropTypes.array,
  onChange: PropTypes.func,
  pageContext: PropTypes.object,
  /* Connect */
  fetchWebInteractive: PropTypes.func.isRequired,
  fetchWebInteractives: PropTypes.func.isRequired,
  saveCache: PropTypes.func.isRequired,
  clearCache: PropTypes.func.isRequired,
  options: PropTypes.array.isRequired,
  webInteractives: PropTypes.object.isRequired,
  defaultOptions: PropTypes.array.isRequired
};
WebInteractiveSelect.defaultProps = {
  extraOptions: [],
  defaultOptions: [],
  required: false,
  pageContext: {}
};
export default connect(mapStateToProps, mapDispatchToProps)(WebInteractiveSelect);